import './applicantSubmitPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { red } from '@mui/material/colors';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useMask } from '@react-input/mask';
import { isValidDate } from '@thesavingsgroup/date-utils/isValidDate';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useEffect, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import {
  dateOfBirthValidator,
  ssnValidator,
} from '../../../components/utils/inputValidators';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    addCoapplicant,
    dateOfBirth,
    isSubmittingApplication,
    clearSubmitApplicationFailure,
    setDateOfBirth,
    submitApplication,
    submitApplicationFailedMessage,
  } = useApplicantStore();

  const { coapplicantDateOfBirth, setCoapplicantDateOfBirth } =
    useCoapplicantStore();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      dateOfBirthValue: dateOfBirth || '',
      ssnValue: '',
      coapplicantDateOfBirthValue: coapplicantDateOfBirth || '',
      coapplicantSsnValue: '',
      termsValue: false,
    },
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const shouldIncludeCoapplicant = useMemo(
    (): boolean => addCoapplicant === YES,
    [addCoapplicant],
  );

  const dateOfBirthMask = useMask({
    mask: '__/__/____',
    replacement: {
      _: /\d/,
    },
  });

  const ssnMask = useMask({
    mask: '___-__-____',
    replacement: {
      _: /\d/,
    },
  });

  const coapplicantDateOfBirthMask = useMask({
    mask: '__/__/____',
    replacement: {
      _: /\d/,
    },
  });

  const coapplicantSsnMask = useMask({
    mask: '___-__-____',
    replacement: {
      _: /\d/,
    },
  });

  const dateOfBirthValidation = {
    required: t('validation.dateOfBirth.required'),
    pattern: {
      value: dateOfBirthValidator,
      message: t('validation.dateOfBirth.invalid'),
    },
    validate: (value: string): string | boolean => {
      // Check if the date of birth is a valid date
      if (!isValidDate(value)) {
        return t('validation.dateOfBirth.invalid');
      }

      const dateOfBirthParsed = Date.parse(value);

      // Check if the date of birth is a future date
      const currentDate = new Date();
      if (new Date(dateOfBirthParsed) > currentDate) {
        return t('validation.dateOfBirth.futureDate');
      }

      const hundredYearsAgo = new Date();
      hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);

      // Check if the date of birth is 100 years ago or more
      if (new Date(dateOfBirthParsed) <= hundredYearsAgo) {
        return t('validation.dateOfBirth.invalid');
      }

      return true;
    },
  };

  const ssnValidation = {
    required: t('validation.ssn.required'),
    pattern: {
      value: ssnValidator,
      message: t('validation.ssn.invalid'),
    },
  };

  const termsValidation = {
    required: t('validation.terms.required'),
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      dateOfBirthValue,
      ssnValue,
      coapplicantDateOfBirthValue,
      coapplicantSsnValue,
    } = values;

    if (!shouldIncludeCoapplicant && (!dateOfBirthValue || !ssnValue)) {
      return;
    }

    if (
      shouldIncludeCoapplicant &&
      (!coapplicantDateOfBirthValue || !coapplicantSsnValue)
    ) {
      return;
    }

    setDateOfBirth(dateOfBirthValue);

    if (shouldIncludeCoapplicant) {
      setCoapplicantDateOfBirth(coapplicantDateOfBirthValue);
    }

    const successfullySubmitted = await submitApplication(
      ssnValue,
      coapplicantSsnValue,
    );

    if (successfullySubmitted) {
      navigate(next());
    }
  };

  const checkboxClicked = (): void => {
    setValue('termsValue', !getValues('termsValue'));

    // Trigger the validation for the checkbox when toggled.
    // This is to ensure that the error message is removed without needing to click outside of the element
    // because we normally validate the form onBlur.
    trigger('termsValue');
  };

  const checkboxClassName = useMemo(
    (): string =>
      [
        'checkbox-described',
        watch('termsValue') && 'checkbox-described-checked',
        !isEmpty(errors.termsValue) && 'checkbox-described-error',
      ]
        .filter(Boolean)
        .join(' '),
    [watch('termsValue'), errors.termsValue],
  );

  // on unmount, clear the submit application failure message
  useEffect(
    (): (() => void) => (): void => {
      clearSubmitApplicationFailure();
    },
    [],
  );

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-submit-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicantSubmit.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantSubmit.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={locator.pages.applicantSubmit.header.drawerButton}
            type="button"
            onClick={(): void => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantSubmit.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantSubmit.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantSubmit.form.h1}>
                {t('ApplicantSubmitPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantSubmit.form.h2}>
                {t('ApplicantSubmitPage.subtitle')}
              </h2>

              {!isEmpty(submitApplicationFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicantSubmit.alert.error}
                    icon={false}
                    severity="error"
                  >
                    {submitApplicationFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('dateOfBirthValue', dateOfBirthValidation)}
                    color="primary"
                    error={!isEmpty(errors.dateOfBirthValue)}
                    inputRef={dateOfBirthMask}
                    label={t('ApplicantSubmitPage.dateOfBirth')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantSubmit.form.dateOfBirthInput,
                        inputMode: 'numeric',
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.dateOfBirthValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantSubmit.form.dateOfBirthInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.dateOfBirthValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('ssnValue', ssnValidation)}
                    color="primary"
                    error={!isEmpty(errors.ssnValue)}
                    inputRef={ssnMask}
                    label={t('ApplicantSubmitPage.socialSecurityNumber')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantSubmit.form.ssnInput,
                        inputMode: 'numeric',
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.ssnValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantSubmit.form.ssnInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.ssnValue.message)}
                    </span>
                  )}
                </div>
              </div>

              {shouldIncludeCoapplicant && (
                <div
                  data-testid={
                    locator.pages.applicantSubmit.form.coapplicantSectionRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.applicantSubmit.form.coapplicantSectionH3
                    }
                  >
                    {t('ApplicantSubmitPage.coapplicant')}
                  </h3>

                  <div>
                    <TextField
                      {...register(
                        'coapplicantDateOfBirthValue',
                        dateOfBirthValidation,
                      )}
                      color="primary"
                      error={!isEmpty(errors.coapplicantDateOfBirthValue)}
                      inputRef={coapplicantDateOfBirthMask}
                      label={t('ApplicantSubmitPage.dateOfBirth')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantSubmit.form
                              .coapplicantDateOfBirthInput,
                          inputMode: 'numeric',
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.coapplicantDateOfBirthValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantSubmit.form
                            .coapplicantDateOfBirthInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.coapplicantDateOfBirthValue.message)}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('coapplicantSsnValue', ssnValidation)}
                      color="primary"
                      error={!isEmpty(errors.coapplicantSsnValue)}
                      inputRef={coapplicantSsnMask}
                      label={t('ApplicantSubmitPage.socialSecurityNumber')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantSubmit.form
                              .coapplicantSsnInput,
                          inputMode: 'numeric',
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.coapplicantSsnValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantSubmit.form
                            .coapplicantSsnInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.coapplicantSsnValue.message)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '3rem' }}>
                <CssBaseline>
                  <Container
                    className={checkboxClassName}
                    onClick={checkboxClicked}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register('termsValue', termsValidation)}
                          checked={watch('termsValue', false)}
                          data-testid={
                            locator.pages.applicantSubmit.form.termsCheckbox
                          }
                        />
                      }
                      label={
                        shouldIncludeCoapplicant
                          ? t('ApplicantSubmitPage.agreeToFollowingCoapplicant')
                          : t('ApplicantSubmitPage.agreeToFollowing')
                      }
                      // Allows the higher level container to handle the click event
                      style={{ pointerEvents: 'none' }}
                    />
                    <p>
                      <Trans
                        components={{
                          terms: (
                            <a
                              data-testid={
                                locator.pages.applicantSubmit.form.termsLink
                              }
                              href="https://www.thesavingsgroup.com/terms-of-use/"
                              rel="noreferrer"
                              target="_blank"
                              onClick={(event): void => {
                                event.stopPropagation();
                              }}
                            />
                          ),
                          privacy: (
                            <a
                              data-testid={
                                locator.pages.applicantSubmit.form.privacyLink
                              }
                              href="https://www.thesavingsgroup.com/privacy-policy/"
                              rel="noreferrer"
                              target="_blank"
                              onClick={(event): void => {
                                event.stopPropagation();
                              }}
                            />
                          ),
                        }}
                        i18nKey={'ApplicantSubmitPage.acknowledgement'}
                      />
                    </p>
                  </Container>
                </CssBaseline>

                {!isEmpty(errors.termsValue) && (
                  <span
                    data-testid={
                      locator.pages.applicantSubmit.form.termsCheckboxError
                    }
                    style={{ fontSize: '12px', color: red[700] }}
                  >
                    {toString(errors.termsValue.message)}
                  </span>
                )}
              </div>

              <div style={{ marginTop: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={locator.pages.applicantSubmit.form.submitButton}
                  disabled={!isValid}
                  loading={isSubmittingApplication}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantSubmitPage.submit')}
                </Button>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantSubmit.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantSubmitPage';
